import { useMemo } from 'react';
import { useAuth } from 'react-oidc-context';
import { Navigate, Route, Routes } from 'react-router-dom';
import type { ApiClient } from './api.js';
import { useRuntimeConfig } from './context.js';
import { cognitoSignOutUrl } from './auth.js';
import { AccessDashboard } from './pages/AccessDashboard.js';
import { AdminPage } from './pages/AdminPage.js';
import { MfaSetup } from './pages/MfaSetup.js';
import { Layout } from './components/Layout.js';

interface AppProps {
  apiFactory: (getToken: () => string | undefined) => ApiClient;
}

const ADMIN_GROUP = 'benilar-proserv-admins';

export function App({ apiFactory }: AppProps): JSX.Element {
  const auth = useAuth();
  const config = useRuntimeConfig();

  const api = useMemo(
    () => apiFactory(() => auth.user?.access_token),
    [apiFactory, auth.user?.access_token],
  );

  if (auth.isLoading) {
    return <div className="center">Signing you in…</div>;
  }

  if (auth.error) {
    return (
      <div className="center">
        <h2>Sign-in error</h2>
        <p>{auth.error.message}</p>
        <button onClick={() => void auth.signinRedirect()}>Try again</button>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <div className="center login">
        <h1>Benilar ProServ Access</h1>
        <p>Sign in with your Benilar Google Workspace account.</p>
        <button className="primary" onClick={() => void auth.signinRedirect()}>
          Sign in with Google Workspace
        </button>
      </div>
    );
  }

  const groups = extractGroups(auth.user?.access_token);
  const isAdmin = groups.includes(ADMIN_GROUP);

  const signOut = (): void => {
    void auth.removeUser();
    window.location.href = cognitoSignOutUrl(config);
  };

  return (
    <Layout
      email={(auth.user?.profile.email as string) ?? 'unknown'}
      isAdmin={isAdmin}
      onSignOut={signOut}
    >
      <Routes>
        <Route path="/" element={<AccessDashboard api={api} />} />
        <Route path="/mfa/setup" element={<MfaSetup api={api} />} />
        {isAdmin && <Route path="/admin" element={<AdminPage api={api} />} />}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

/** Decode the groups claim from the JWT without verifying (display-only). */
function extractGroups(accessToken?: string): string[] {
  if (!accessToken) return [];
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1] ?? '')) as {
      'cognito:groups'?: string[];
    };
    return payload['cognito:groups'] ?? [];
  } catch {
    return [];
  }
}
